import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { viewerUrl, duration } from '../lib/format';
import { ActivityList } from './ActivityList';

export default function LinkVisitsPage() {
  const { projectId = '', linkId = '' } = useParams();
  const q = useQuery({
    queryKey: ['linkVisits', projectId, linkId],
    queryFn: () => api.linkVisits(projectId, linkId),
    refetchInterval: 30_000,
  });
  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
  });

  if (q.isLoading) return <p className="text-soft">Loading…</p>;
  if (!q.data) return <p className="text-soft">Link not found.</p>;
  const { link, visits, raw } = q.data;
  const docTitles = Object.fromEntries(
    (project.data?.docs ?? []).map((d) => [d.docId, d.title]),
  );
  const totalSeconds = visits.reduce((a, v) => a + v.totalSeconds, 0);

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <Link to="/" className="rw-heading text-xs text-soft hover:text-white">
          Projects
        </Link>
        <span className="text-mid">/</span>
        <Link
          to={`/p/${projectId}`}
          className="rw-heading text-xs text-soft hover:text-white"
        >
          {project.data?.project.name ?? '…'}
        </Link>
        <span className="text-mid">/</span>
        <h1 className="rw-heading text-sm text-white">{link.label}</h1>
        <button
          onClick={() => {
            void navigator.clipboard.writeText(viewerUrl(link.slug));
            toast.success('Copied');
          }}
          className="rw-btn-ghost normal-case tracking-normal"
          style={{ textTransform: 'none', letterSpacing: 0 }}
        >
          <Copy className="h-3 w-3" /> /v/{link.slug}
        </button>
        {!link.active && (
          <span className="bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
            disabled
          </span>
        )}
      </div>

      <div className="mb-8 grid grid-cols-2 gap-3 sm:max-w-2xl sm:grid-cols-4">
        <Stat label="Visits" value={String(link.visitCount ?? 0)} />
        <Stat
          label="Unique viewers"
          value={String(new Set(visits.map((v) => v.email ?? v.visitId)).size)}
        />
        <Stat label="Total time" value={duration(totalSeconds)} />
        <Stat label="Downloads" value={String(link.downloadCount ?? 0)} />
      </div>

      <ActivityList
        visits={visits.map((v) => ({ ...v, projectId }))}
        raw={raw}
        docTitles={docTitles}
        emptyText={`No visits yet. Send ${viewerUrl(link.slug)} to ${link.label}.`}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rw-card p-4">
      <div className="font-heading text-2xl font-semibold text-ink">{value}</div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-mid">{label}</div>
    </div>
  );
}
